import { useState, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { getPendingClassFeedbacks } from "services/api/class-feedback/classFeedbackService";
import {
  IRestructuredPendingFeedbacksAPIResponse,
  PendingClassRatingStatus
} from "services/api/class-feedback/classFeedbackService.types";
import {
  IRateClassModalData,
  IUseRateClassProps,
  IUseRateClassReturnValues,
  ModalSource
} from "./use-rate-class.types";
import { useReRunOnTabFocus } from "hooks/use-rerun-on-tab-focus/use-rerun-on-tab-focus";

export const useRateClass = ({
  primaryUser,
  showLatestPendingReviewModal
}: IUseRateClassProps): IUseRateClassReturnValues => {
  const [pendingFeedbacks, setPendingFeedbacks] =
    useState<IRestructuredPendingFeedbacksAPIResponse>({
      feedbacks: [],
      metaData: {
        ratings: [],
        tagsByRating: {}
      }
    });
  const [modalData, setModalData] = useState<IRateClassModalData | null>(null);

  const fetchPendingClassFeedbacks = useCallback(async () => {
    if (!primaryUser) {
      return;
    }
    try {
      const data = await getPendingClassFeedbacks(primaryUser.access_token);
      setPendingFeedbacks(data);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [primaryUser]);
  // Refetches data when tab comes back in focus and modal is not opened
  useReRunOnTabFocus(fetchPendingClassFeedbacks, { enabled: !modalData });

  useEffect(() => {
    // fetch pending class ratings
    fetchPendingClassFeedbacks();
  }, [fetchPendingClassFeedbacks]);

  useEffect(() => {
    if (
      pendingFeedbacks.feedbacks.length === 0 ||
      !showLatestPendingReviewModal
    ) {
      return;
    }
    // show most recent non skipped and dismissed pending feedback
    const feedbackToShow = pendingFeedbacks.feedbacks.find(
      ({ previousState }) => {
        return (
          !previousState ||
          (previousState.status !== PendingClassRatingStatus.DISMISSED &&
            previousState.status === PendingClassRatingStatus.SKIPPED)
        );
      }
    );
    if (!feedbackToShow) {
      return;
    }
    setModalData({ ...feedbackToShow, modalSource: ModalSource.TAB_VIEWED });
  }, [pendingFeedbacks, showLatestPendingReviewModal]);

  const openRatingModal = useCallback((data: IRateClassModalData) => {
    setModalData(data);
  }, []);

  const closeRatingModal = useCallback(() => {
    setModalData(null);

    setTimeout(() => {
      fetchPendingClassFeedbacks();
    }, 500);
  }, [fetchPendingClassFeedbacks]);

  return {
    pendingClassRatings: pendingFeedbacks.feedbacks,
    metaData: pendingFeedbacks.metaData,
    modalData,
    isModalOpen: !!modalData,
    openRatingModal,
    closeRatingModal
  };
};
